import { client } from '@api/rest/client';
import i18n from '@config/i18n';
import { socketIOSettings } from '@config/socket.io';
import { API_ENDPOINT } from '@constants/env';
import {
  BackfillRunPollStatus,
  TicketingSource,
} from '@conversed/rest/src/contracts/article-architect/article-architect.enums';
import { ARTICLE_ARCHITECT_EVENTS } from '@conversed/shared/src/central/constants/socket';
import {
  type ArticleArchitectCompletedData,
  type ArticleArchitectErrorData,
  type ArticleArchitectProgressData,
  ArticleArchitectProgressPhase,
  type ArticleArchitectTopdeskGenerationEstimateData,
} from '@conversed/shared/src/central/dto/article-architect.dto';
import { findArticleArchitectErrorCodeKey } from '@helpers/errorHelper';
import {
  reconnectAfterRetryHint,
  reconnectAfterServerDisconnect,
} from '@helpers/socketHelpers';
import { useMainSelector } from '@hooks/useMainSelector';
import { useCallback, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import {
  GenerateStatus,
  type ProgressStep,
} from './useArticleArchitectUploads';

type TicketIncrementalCount = Awaited<
  ReturnType<typeof client.articleArchitect.getTicketIncrementalCount>
>['data'];

type BackfillRunStatusResponse = Awaited<
  ReturnType<typeof client.articleArchitect.getBackfillRunStatus>
>['data'];

const { PLATFORM_TO_BACKEND, BACKEND_TO_PLATFORM } = ARTICLE_ARCHITECT_EVENTS;

// * Generic failure copy per leg. The estimate catch persists ESTIMATE_FAILED and
// * the generate catch GENERATION_FAILED, so the fallback matches the leg that
// * failed rather than always blaming the estimate.
export const ESTIMATE_FAILED_ERROR_CODE = 'ARTICLE_ARCHITECT_ESTIMATE_FAILED';
export const GENERATION_FAILED_ERROR_CODE =
  'ARTICLE_ARCHITECT_GENERATION_FAILED';

// * Correctness fallback for a dropped socket: while a run is in progress the
// * overlay polls the persisted backfill-run status at this interval, so a terminal
// * failure still surfaces even when the socket ERROR event never arrives.
const STATUS_POLL_INTERVAL_MS = 10000;

interface RunFailureKeyInput {
  awaitingEstimate: boolean;
  errorCode?: string;
  codeHasTranslation: (translationKey: string) => boolean;
}

// * Chooses the `error.codes.*` translation key for a run failure. It prefers an
// * `ARTICLE_ARCHITECT_*` backend error code that has a translation; a generic code
// * such as NOT_FOUND or an untranslated one falls back to the copy of the leg
// * that failed - the estimate leg while awaiting the estimate, the generate leg
// * once the run confirmed. The status poll persists a raw message
// * in the run doc, never an error code, so the fallback is what it always uses.
export const resolveRunFailureKey = ({
  awaitingEstimate,
  errorCode,
  codeHasTranslation,
}: RunFailureKeyInput): string => {
  const codeKey = findArticleArchitectErrorCodeKey(
    errorCode,
    codeHasTranslation,
  );

  if (codeKey) {
    return codeKey;
  }

  const fallbackCode = awaitingEstimate
    ? ESTIMATE_FAILED_ERROR_CODE
    : GENERATION_FAILED_ERROR_CODE;

  return `error.codes.${fallbackCode}`;
};

// * Translates a run failure into a display message, mirroring the toast helper's
// * `error.codes.*` lookup, with a leg-aware fallback (see resolveRunFailureKey).
const resolveRunFailureMessage = (
  awaitingEstimate: boolean,
  errorCode?: string,
): string => {
  return i18n.t(
    resolveRunFailureKey({
      awaitingEstimate,
      errorCode,
      codeHasTranslation: (translationKey) => i18n.exists(translationKey),
    }),
  );
};

// * True when a socket event belongs to a run other than the active one. A run's
// * events all carry its runId (COMPLETED carries it as `uploadId`). When both the
// * active runId and the event's runId are known and differ, the event is a foreign
// * run's - a prior run still emitting, or another flow sharing the user room - and
// * must be ignored. Before the estimate arrives the active runId is null, so the
// * check cannot disambiguate yet; the backend staleness fix removes the case that
// * produced a live foreign run, leaving this as defense-in-depth.
export const isForeignRunEvent = (
  activeRunId: string | null,
  eventRunId?: string,
): boolean => {
  return (
    activeRunId !== null &&
    eventRunId !== undefined &&
    eventRunId !== activeRunId
  );
};

// * The four progress phases only a generation run emits. An estimate run never
// * drafts, so any of these arriving while the overlay still awaits the estimate
// * belongs to a concurrent generation sharing the user room.
const GENERATION_ONLY_PHASES = new Set<ArticleArchitectProgressPhase>([
  ArticleArchitectProgressPhase.Generating,
  ArticleArchitectProgressPhase.ArticleDrafted,
  ArticleArchitectProgressPhase.CheckingConflicts,
  ArticleArchitectProgressPhase.ConflictDetected,
]);

// * True when a generation-only progress phase arrives while the overlay is still
// * awaiting the estimate. The estimate emits no such phase, so the event is a
// * concurrent generation's and must not render in the estimate view - the runId
// * guard cannot catch it when the two runs share a reused runId.
export const isGenerationPhaseDuringEstimate = (
  awaitingEstimate: boolean,
  phase: ArticleArchitectProgressPhase,
): boolean => {
  return awaitingEstimate && GENERATION_ONLY_PHASES.has(phase);
};

// * The single, in-place-updated embedding step. Its label key identifies the
// * step so a later Embedding event replaces it rather than appending a new one.
const EMBEDDING_STEP_KEY = 'article-architect.progress.step.embedding';

// * The single, in-place-updated ticket classifier step, keyed like the
// * embedding step.
const CLASSIFYING_STEP_KEY = 'article-architect.progress.step.classifying';

// * The phases that report a live count, so each updates one step in place
// * rather than appending one per event.
const LIVE_COUNT_PHASES = new Set<ArticleArchitectProgressPhase>([
  ArticleArchitectProgressPhase.Classifying,
  ArticleArchitectProgressPhase.Embedding,
]);

// * Coarse phase -> step mapping for a TopDesk generation run. Only the phases
// * the backfill emits are mapped; the per-file phases (reading, pages, words)
// * never fire for this source, so an unmapped phase is skipped rather than
// * rendered with a file-oriented label.
export const buildTopdeskStep = (
  data: ArticleArchitectProgressData,
  articleIndex?: number,
): ProgressStep | null => {
  switch (data.phase) {
    case ArticleArchitectProgressPhase.Classifying:
      return {
        labelKey: CLASSIFYING_STEP_KEY,
        labelParams: { processed: data.processed ?? 0, total: data.total ?? 0 },
        isDone: false,
      };
    case ArticleArchitectProgressPhase.Embedding:
      return {
        labelKey: EMBEDDING_STEP_KEY,
        labelParams: { processed: data.processed ?? 0, total: data.total ?? 0 },
        isDone: false,
      };
    case ArticleArchitectProgressPhase.Generating:
      return {
        labelKey: 'article-architect.progress.step.generating',
        isDone: false,
      };
    case ArticleArchitectProgressPhase.ArticleDrafted:
      return {
        labelKey: 'article-architect.progress.step.article-drafted',
        labelParams: { index: articleIndex ?? '', title: data.title ?? '' },
        // * The article is already saved when this event fires.
        isDone: true,
      };
    case ArticleArchitectProgressPhase.CheckingConflicts:
      return {
        labelKey: 'article-architect.progress.step.checking-conflicts',
        isDone: false,
      };
    case ArticleArchitectProgressPhase.ConflictDetected:
      return {
        labelKey: 'article-architect.progress.step.conflict-detected',
        labelParams: {
          title: data.title ?? '',
          conflictingTitle: data.conflictingTitle ?? '',
        },
        isDone: true,
        isWarning: true,
      };
    default:
      return null;
  }
};

// * Replaces the last step when it is the same live-count step, so the count
// * updates in place. Otherwise every earlier step is done and the live-count step
// * is appended.
export const mergeLiveCountStep = (
  previousSteps: ProgressStep[],
  liveCountStep: ProgressStep,
): ProgressStep[] => {
  const lastStep = previousSteps[previousSteps.length - 1];

  if (lastStep?.labelKey === liveCountStep.labelKey) {
    return [...previousSteps.slice(0, -1), liveCountStep];
  }

  return [
    ...previousSteps.map((step) => ({ ...step, isDone: true })),
    liveCountStep,
  ];
};

const buildSummaryStep = (
  data: ArticleArchitectCompletedData,
): ProgressStep => {
  const conflictsDetected = data.conflictsDetected ?? 0;

  if (conflictsDetected > 0) {
    return {
      labelKey: 'article-architect.progress.step.articles-ready-with-conflicts',
      labelParams: {
        articles: data.articlesGenerated,
        conflicts: conflictsDetected,
      },
      isDone: true,
    };
  }

  return {
    labelKey: 'article-architect.progress.step.articles-generated',
    labelParams: { count: data.articlesGenerated },
    isDone: true,
  };
};

// * Drives the Phase-2 knowledge article generation from ingested TopDesk
// * incidents. Generation is superAdmin-only: a superAdmin starts a run, receives
// * a cost estimate, then confirms it. Owns its own /sockets/platform connection,
// * kept separate from the pull hook so a pull never drives the generation
// * progress view and back.
export const useTopdeskArticleGeneration = () => {
  const {
    login: { accessToken },
    user: { currentUser },
  } = useMainSelector();
  const isSuperAdmin = currentUser?.isSuperAdmin === true;

  const socketRef = useRef<Socket | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const runIdRef = useRef<string | null>(null);
  // * Guards the terminal handlers against stale events from a previous run that
  // * arrives after the user has left and returned to the card.
  const hasInitiatedRef = useRef(false);
  // * Mirrors awaitingEstimate for the once-registered socket handlers, so the
  // * PROGRESS handler can drop a concurrent generation's drafted-article events
  // * from the estimate overlay without re-subscribing on every state change.
  const awaitingEstimateRef = useRef(false);
  // * Mirrors generationStatus for the poll's idempotency check: an in-flight poll
  // * that resolves after a socket terminal must not overwrite the final state.
  const generationStatusRef = useRef<GenerateStatus>(GenerateStatus.Idle);

  const [incremental, setIncremental] = useState<TicketIncrementalCount | null>(
    null,
  );
  const [estimate, setEstimate] =
    useState<ArticleArchitectTopdeskGenerationEstimateData | null>(null);
  const [awaitingEstimate, setAwaitingEstimate] = useState(false);
  const [generationStatus, setGenerationStatus] = useState<GenerateStatus>(
    GenerateStatus.Idle,
  );
  const [progressSteps, setProgressSteps] = useState<ProgressStep[]>([]);
  const [generationResult, setGenerationResult] = useState<{
    articlesGenerated: number;
    conflictsDetected: number;
    couldNotProcess: number;
  } | null>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);

  const resetGenerationState = useCallback(() => {
    setProgressSteps([]);
    setGenerationResult(null);
    setGenerationError(null);
    setEstimate(null);
    setAwaitingEstimate(false);
    runIdRef.current = null;
  }, []);

  useEffect(() => {
    generationStatusRef.current = generationStatus;
  }, [generationStatus]);

  useEffect(() => {
    awaitingEstimateRef.current = awaitingEstimate;
  }, [awaitingEstimate]);

  // * Idempotent terminal transition for a failed/aborted poll result. Reuses the
  // * socket ERROR display path (all steps done, Error status, a surfaced reason)
  // * and clears awaitingEstimate so the overlay leaves the Generating view. Skips
  // * the write when a socket terminal already ended the run.
  const applyPollTerminalFailure = useCallback((errorCode?: string) => {
    if (
      generationStatusRef.current === GenerateStatus.Error ||
      generationStatusRef.current === GenerateStatus.Completed
    ) {
      return;
    }

    // * Capture the leg before clearing awaitingEstimate, so the message matches the
    // * leg that actually failed.
    const awaitingEstimateAtFailure = awaitingEstimateRef.current;

    setProgressSteps((previousSteps) =>
      previousSteps.map((step) => ({ ...step, isDone: true })),
    );
    setAwaitingEstimate(false);
    setGenerationStatus(GenerateStatus.Error);
    setGenerationError(
      resolveRunFailureMessage(awaitingEstimateAtFailure, errorCode),
    );
  }, []);

  const handleBackfillPollResult = useCallback(
    (data: BackfillRunStatusResponse) => {
      if (
        data.status === BackfillRunPollStatus.Failed ||
        data.status === BackfillRunPollStatus.Aborted
      ) {
        applyPollTerminalFailure(data.error);
      }
    },
    [applyPollTerminalFailure],
  );

  // * On mount, recover an in-progress run after a page reload: a `running` poll
  // * resumes the overlay so the live poll below (and a reconnected socket) drive it
  // * to completion. A terminal run is left as history, matching the pull hook - an
  // * old failure is not resurfaced as a live error on every reload.
  useEffect(() => {
    client.articleArchitect
      .getBackfillRunStatus({ query: { source: TicketingSource.Topdesk } })
      .then((response) => {
        if (!response.data) {
          return;
        }

        if (response.data.status === BackfillRunPollStatus.Running) {
          hasInitiatedRef.current = true;
          setGenerationStatus(GenerateStatus.Generating);
        }
      })
      .catch((fetchError) => {
        console.error(
          '[TopdeskGeneration] Failed to fetch backfill run status:',
          fetchError,
        );
      });
  }, []);

  useEffect(() => {
    if (!awaitingEstimate && generationStatus !== GenerateStatus.Generating) {
      return;
    }

    pollIntervalRef.current = setInterval(() => {
      client.articleArchitect
        .getBackfillRunStatus({ query: { source: TicketingSource.Topdesk } })
        .then((response) => {
          if (!response.data) {
            return;
          }

          handleBackfillPollResult(response.data);
        })
        .catch((pollError) => {
          console.error(
            '[TopdeskGeneration] Failed to poll backfill run status:',
            pollError,
          );
        });
    }, STATUS_POLL_INTERVAL_MS);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [awaitingEstimate, generationStatus, handleBackfillPollResult]);

  // * Only the superAdmin card shows the count, and each fetch makes Core query
  // * TopDesk once per backfill pair, so other viewers skip it.
  useEffect(() => {
    if (!isSuperAdmin) {
      return;
    }

    client.articleArchitect
      .getTicketIncrementalCount({
        query: { source: TicketingSource.Topdesk },
      })
      .then((response) => {
        if (!response.data) {
          return;
        }

        setIncremental(response.data);
      })
      .catch((fetchError) => {
        console.error(
          '[TopdeskGeneration] Failed to fetch incremental count:',
          fetchError,
        );
      });
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    if (socketRef.current?.connected) {
      return;
    }

    socketRef.current = io(API_ENDPOINT, {
      ...socketIOSettings,
      path: '/sockets/platform',
      auth: {
        token: accessToken,
      },
    });
    reconnectAfterServerDisconnect(socketRef.current);
    const cancelRetryReconnect = reconnectAfterRetryHint(socketRef.current);

    socketRef.current.on(
      BACKEND_TO_PLATFORM.TOPDESK_GENERATION_ESTIMATE,
      (data: ArticleArchitectTopdeskGenerationEstimateData) => {
        if (!hasInitiatedRef.current) {
          return;
        }

        runIdRef.current = data.runId;
        setEstimate(data);
        setAwaitingEstimate(false);
      },
    );

    socketRef.current.on(
      BACKEND_TO_PLATFORM.PROGRESS,
      (data: ArticleArchitectProgressData) => {
        if (!hasInitiatedRef.current) {
          return;
        }

        if (isForeignRunEvent(runIdRef.current, data.runId)) {
          return;
        }

        if (
          isGenerationPhaseDuringEstimate(
            awaitingEstimateRef.current,
            data.phase,
          )
        ) {
          return;
        }

        // * Classifying and Embedding report a live count, so each updates a
        // * single step in place rather than appending one per event.
        if (LIVE_COUNT_PHASES.has(data.phase)) {
          const liveCountStep = buildTopdeskStep(data);

          if (!liveCountStep) {
            return;
          }

          setProgressSteps((previousSteps) =>
            mergeLiveCountStep(previousSteps, liveCountStep),
          );

          return;
        }

        const newStep = buildTopdeskStep(
          data,
          data.phase === ArticleArchitectProgressPhase.ArticleDrafted
            ? data.processed
            : undefined,
        );

        if (!newStep) {
          return;
        }

        if (
          data.phase === ArticleArchitectProgressPhase.ArticleDrafted ||
          data.phase === ArticleArchitectProgressPhase.ConflictDetected
        ) {
          setProgressSteps((previousSteps) => [...previousSteps, newStep]);

          return;
        }

        if (data.phase === ArticleArchitectProgressPhase.CheckingConflicts) {
          setProgressSteps((previousSteps) => [
            ...previousSteps.map((step) => ({ ...step, isDone: true })),
            newStep,
          ]);

          return;
        }

        setProgressSteps((previousSteps) => {
          const updated = previousSteps.map((step, stepIndex) =>
            stepIndex === previousSteps.length - 1
              ? { ...step, isDone: true }
              : step,
          );

          return [...updated, newStep];
        });
      },
    );

    socketRef.current.on(
      BACKEND_TO_PLATFORM.COMPLETED,
      (data: ArticleArchitectCompletedData) => {
        if (!hasInitiatedRef.current) {
          return;
        }

        if (isForeignRunEvent(runIdRef.current, data.uploadId)) {
          return;
        }

        setProgressSteps((previousSteps) => [
          ...previousSteps.map((step) => ({ ...step, isDone: true })),
          buildSummaryStep(data),
        ]);
        setGenerationStatus(GenerateStatus.Completed);
        setGenerationResult({
          articlesGenerated: data.articlesGenerated,
          conflictsDetected: data.conflictsDetected ?? 0,
          couldNotProcess: data.couldNotProcess ?? 0,
        });
      },
    );

    socketRef.current.on(
      BACKEND_TO_PLATFORM.ERROR,
      (data: ArticleArchitectErrorData) => {
        if (!hasInitiatedRef.current) {
          return;
        }

        if (isForeignRunEvent(runIdRef.current, data.runId)) {
          return;
        }

        console.error(
          '[TopdeskGeneration] Generation error:',
          data.errorCode,
          data.errorData,
        );
        // * Capture the leg before clearing awaitingEstimate. Clearing it is what
        // * lets getProgressViewStatus render the Error state - left set, it masks
        // * an estimate-leg failure as a still-running Generating view.
        const awaitingEstimateAtFailure = awaitingEstimateRef.current;

        setProgressSteps((previousSteps) =>
          previousSteps.map((step) => ({ ...step, isDone: true })),
        );
        setAwaitingEstimate(false);
        setGenerationStatus(GenerateStatus.Error);
        setGenerationError(
          resolveRunFailureMessage(awaitingEstimateAtFailure, data.errorCode),
        );
      },
    );

    return () => {
      cancelRetryReconnect();

      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [accessToken]);

  // * SuperAdmin path: start phase A, then wait for the estimate before the run
  // * actually generates anything, so no progress view shows yet.
  const startSuperAdminGeneration = useCallback(
    (companyId: string): boolean => {
      if (!socketRef.current?.connected) {
        console.error('[TopdeskGeneration] Socket not connected');

        return false;
      }

      resetGenerationState();
      hasInitiatedRef.current = true;
      setAwaitingEstimate(true);
      socketRef.current.emit(PLATFORM_TO_BACKEND.START_TOPDESK_GENERATION, {
        companyId,
      });

      return true;
    },
    [resetGenerationState],
  );

  const confirmGeneration = useCallback((companyId: string): boolean => {
    const runId = runIdRef.current;

    if (!socketRef.current?.connected || !runId) {
      console.error(
        '[TopdeskGeneration] Cannot confirm: socket or run missing',
      );

      return false;
    }

    setEstimate(null);
    setProgressSteps([]);
    setGenerationStatus(GenerateStatus.Generating);
    socketRef.current.emit(PLATFORM_TO_BACKEND.CONFIRM_TOPDESK_GENERATION, {
      companyId,
      runId,
    });

    return true;
  }, []);

  const cancelEstimate = useCallback(() => {
    hasInitiatedRef.current = false;
    setEstimate(null);
    setAwaitingEstimate(false);
    runIdRef.current = null;
  }, []);

  // * Estimate-leg cancel: tells the backend to drop the pending run, then
  // * resets FE state the same way cancelEstimate does. The generation leg has
  // * no cancel path - the caller only wires this while awaitingEstimate.
  const requestCancel = useCallback((companyId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(PLATFORM_TO_BACKEND.CANCEL_TOPDESK_GENERATION, {
        companyId,
      });
    }

    hasInitiatedRef.current = false;
    setEstimate(null);
    setAwaitingEstimate(false);
    runIdRef.current = null;
  }, []);

  const resetGeneration = useCallback(() => {
    hasInitiatedRef.current = false;
    resetGenerationState();
    setGenerationStatus(GenerateStatus.Idle);
  }, [resetGenerationState]);

  return {
    incremental,
    estimate,
    awaitingEstimate,
    generationStatus,
    progressSteps,
    generationResult,
    generationError,
    startSuperAdminGeneration,
    confirmGeneration,
    cancelEstimate,
    requestCancel,
    resetGeneration,
  };
};
